import { useState } from 'react'
import { clsx } from 'clsx'
import { Calendar, CheckCircle, Clock, ChevronLeft, ChevronRight } from 'lucide-react'

interface EarningsEntry {
  company: string
  ticker: string
  date: string
  quarter: string
  previousEPS: string
  status: 'upcoming' | 'reported'
  colour: string
}

const EARNINGS: EarningsEntry[] = [
  { company: 'NVIDIA', ticker: 'NVDA', date: '2026-05-28', quarter: 'Q1 FY27', previousEPS: '$0.89', status: 'reported', colour: '#76B900' },
  { company: 'Snowflake', ticker: 'SNOW', date: '2026-05-28', quarter: 'Q1 FY27', previousEPS: '$0.20', status: 'reported', colour: '#29B5E8' },
  { company: 'Datadog', ticker: 'DDOG', date: '2026-05-13', quarter: 'Q1 FY26', previousEPS: '$0.46', status: 'reported', colour: '#632CA6' },
  { company: 'Cloudflare', ticker: 'NET', date: '2026-05-07', quarter: 'Q1 FY26', previousEPS: '$0.16', status: 'reported', colour: '#F6821F' },
  { company: 'Palantir', ticker: 'PLTR', date: '2026-05-05', quarter: 'Q1 FY26', previousEPS: '$0.11', status: 'reported', colour: '#101010' },
  { company: 'Salesforce', ticker: 'CRM', date: '2026-06-03', quarter: 'Q1 FY27', previousEPS: '$2.58', status: 'upcoming', colour: '#00A1E0' },
  { company: 'CrowdStrike', ticker: 'CRWD', date: '2026-06-04', quarter: 'Q1 FY27', previousEPS: '$0.93', status: 'upcoming', colour: '#E01F29' },
  { company: 'MongoDB', ticker: 'MDB', date: '2026-06-05', quarter: 'Q1 FY27', previousEPS: '$0.51', status: 'upcoming', colour: '#00ED64' },
  { company: 'Broadcom', ticker: 'AVGO', date: '2026-06-12', quarter: 'Q2 FY26', previousEPS: '$1.42', status: 'upcoming', colour: '#CC092F' },
  { company: 'Apple', ticker: 'AAPL', date: '2026-07-30', quarter: 'Q3 FY26', previousEPS: '$1.65', status: 'upcoming', colour: '#A2AAAD' },
  { company: 'Microsoft', ticker: 'MSFT', date: '2026-07-22', quarter: 'Q4 FY26', previousEPS: '$3.23', status: 'upcoming', colour: '#00A4EF' },
  { company: 'Google', ticker: 'GOOGL', date: '2026-07-28', quarter: 'Q2 FY26', previousEPS: '$2.00', status: 'upcoming', colour: '#4285F4' },
  { company: 'Amazon', ticker: 'AMZN', date: '2026-07-30', quarter: 'Q2 FY26', previousEPS: '$1.26', status: 'upcoming', colour: '#FF9900' },
  { company: 'Meta', ticker: 'META', date: '2026-07-23', quarter: 'Q2 FY26', previousEPS: '$6.03', status: 'upcoming', colour: '#0081FB' },
  { company: 'Tesla', ticker: 'TSLA', date: '2026-07-22', quarter: 'Q2 FY26', previousEPS: '$0.52', status: 'upcoming', colour: '#E31937' },
  { company: 'AMD', ticker: 'AMD', date: '2026-07-28', quarter: 'Q2 FY26', previousEPS: '$0.96', status: 'upcoming', colour: '#ED1C24' },
  { company: 'Intel', ticker: 'INTC', date: '2026-07-23', quarter: 'Q2 FY26', previousEPS: '$0.13', status: 'upcoming', colour: '#0071C5' },
]

const SORTED = [...EARNINGS].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

const TODAY = new Date('2026-04-15')

function daysUntil(dateStr: string): number {
  return Math.round((new Date(dateStr).getTime() - TODAY.getTime()) / (1000 * 60 * 60 * 24))
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/* -------- mini calendar helpers -------- */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function startDay(year: number, month: number): number {
  return new Date(year, month, 1).getDay()
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

function MiniCalendar({ year, month, entries }: { year: number; month: number; entries: EarningsEntry[] }) {
  const total = daysInMonth(year, month)
  const offset = startDay(year, month)
  const cells: (number | null)[] = Array(offset).fill(null)
  for (let d = 1; d <= total; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)

  const earningsByDay: Record<number, EarningsEntry[]> = {}
  entries.forEach(e => {
    const d = new Date(e.date)
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate()
      if (!earningsByDay[day]) earningsByDay[day] = []
      earningsByDay[day].push(e)
    }
  })

  const todayDay = TODAY.getFullYear() === year && TODAY.getMonth() === month ? TODAY.getDate() : -1

  return (
    <div className="card p-3">
      <div className="text-xs font-semibold text-text-primary mb-2 text-center">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-px text-[10px] text-text-muted text-center mb-1">
        {DAY_LABELS.map(d => <div key={d} className="font-medium">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px text-[10px] text-center">
        {cells.map((day, i) => {
          const hasEarnings = day ? earningsByDay[day] : null
          const isToday = day === todayDay
          return (
            <div
              key={i}
              className={clsx(
                'h-7 flex flex-col items-center justify-center rounded relative',
                !day && 'opacity-0',
                isToday && 'ring-1 ring-blue',
              )}
              title={hasEarnings ? hasEarnings.map(e => e.company).join(', ') : undefined}
            >
              <span className={clsx('leading-none', isToday ? 'text-blue font-bold' : 'text-text-secondary')}>
                {day}
              </span>
              {hasEarnings && (
                <div className="flex gap-[2px] mt-[1px]">
                  {hasEarnings.slice(0, 4).map((e, j) => (
                    <span key={j} className="w-[5px] h-[5px] rounded-full inline-block" style={{ backgroundColor: e.colour }} />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function EarningsCalendarPage() {
  const [calMonth, setCalMonth] = useState(TODAY.getMonth())
  const [calYear, setCalYear] = useState(TODAY.getFullYear())
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'reported'>('all')

  const list = SORTED.filter(e => statusFilter === 'all' || e.status === statusFilter)

  const reportedCount = EARNINGS.filter(e => e.status === 'reported').length
  const upcomingCount = EARNINGS.filter(e => e.status === 'upcoming').length

  function prevMonth() {
    if (calMonth === 0) { setCalMonth(11); setCalYear(calYear - 1) }
    else setCalMonth(calMonth - 1)
  }
  function nextMonth() {
    if (calMonth === 11) { setCalMonth(0); setCalYear(calYear + 1) }
    else setCalMonth(calMonth + 1)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Calendar size={24} className="text-amber" /> Earnings Calendar
          </h1>
          <p className="text-sm text-text-secondary mt-1">Upcoming and recent tech company earnings reports</p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="card p-4 text-center">
          <div className="text-2xl font-bold text-text-primary">{EARNINGS.length}</div>
          <div className="text-[10px] text-text-muted mt-1">Companies Tracked</div>
        </div>
        <div className="card p-4 text-center bg-green/10">
          <div className="text-2xl font-bold text-green">{reportedCount}</div>
          <div className="text-[10px] text-text-muted mt-1">Reported</div>
        </div>
        <div className="card p-4 text-center bg-blue/10">
          <div className="text-2xl font-bold text-blue">{upcomingCount}</div>
          <div className="text-[10px] text-text-muted mt-1">Upcoming</div>
        </div>
      </div>

      {/* Calendar + List layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar section */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center justify-between">
            <button type="button" onClick={prevMonth} className="btn-secondary p-1.5"><ChevronLeft size={14} /></button>
            <span className="text-xs font-semibold text-text-primary">{MONTH_NAMES[calMonth]} {calYear}</span>
            <button type="button" onClick={nextMonth} className="btn-secondary p-1.5"><ChevronRight size={14} /></button>
          </div>
          <MiniCalendar year={calYear} month={calMonth} entries={EARNINGS} />

          {/* Quick jump months */}
          <div className="flex flex-wrap gap-1">
            {[4, 5, 6].map(m => (
              <button
                type="button"
                key={m}
                onClick={() => { setCalMonth(m); setCalYear(2026) }}
                className={clsx(
                  'text-[10px] px-2 py-1 rounded',
                  calMonth === m && calYear === 2026 ? 'bg-blue text-white' : 'bg-card-alt text-text-secondary hover:text-text-primary',
                )}
              >
                {MONTH_NAMES[m].slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Earnings list */}
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2">
            <select
              title=""
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
              className="input w-auto py-2 text-xs"
            >
              <option value="all">All ({EARNINGS.length})</option>
              <option value="upcoming">Upcoming ({upcomingCount})</option>
              <option value="reported">Reported ({reportedCount})</option>
            </select>
          </div>

          <div className="space-y-2">
            {list.map(entry => {
              const days = daysUntil(entry.date)
              return (
                <div key={entry.ticker} className="card card-hover p-3 flex items-center gap-3">
                  {/* Colour dot */}
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: entry.colour }} />

                  {/* Company info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-text-primary">{entry.company}</span>
                      <span className="text-[10px] text-text-muted font-mono">{entry.ticker}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px] text-text-muted mt-0.5">
                      <span>{entry.quarter}</span>
                      <span>Prev EPS: <span className="text-text-secondary font-medium">{entry.previousEPS}</span></span>
                    </div>
                  </div>

                  {/* Date + status */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="text-xs text-text-secondary font-medium">{formatDate(entry.date)}</div>
                    {entry.status === 'reported' ? (
                      <div className="flex items-center gap-1 text-[10px] text-green font-medium">
                        <CheckCircle size={12} /> Reported
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-[10px] text-blue font-medium">
                        <Clock size={12} /> {days}d away
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
